import { cn } from '@immo/ui-kit';
import { FLEET_HEALTH_WARN_PERCENT, type FleetHostHealth } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import {
  clampPercent,
  formatFactor,
  isNearlyFull,
  LOAD_BAR_SCALE,
  limitsOf,
  loadOf,
  percentOf,
  temperatureOf,
} from './health-figures';
import { HealthBar } from './health-bar';

type Props = {
  readonly health: FleetHostHealth | null;
  /** Only the bars dim: a dimmed warning would be weaker on the machine nobody is watching. */
  readonly stale: boolean;
};

/** The four tile figures: disk, memory, temperature, load. */
export function TileHealthRow({ health, stale }: Props) {
  const t = useTranslations('pages.admin.fleet.health');
  const limits = limitsOf(health);
  const barClass = stale ? 'opacity-50' : '';

  const disk = percentOf(health?.disk ?? null);
  const memory = percentOf(health?.memory ?? null);
  const temperature = temperatureOf(health);
  const load = loadOf(health);

  const diskFull = disk.kind === 'value' && isNearlyFull(disk.percent);
  const memoryFull = memory.kind === 'value' && isNearlyFull(memory.percent);

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[repeat(4,minmax(0,220px))]">
      <TileFigure
        label={diskFull ? t('disk-full') : t('disk')}
        value={disk.kind === 'value' ? t('percent-value', { percent: disk.percent }) : null}
        warning={diskFull}
        bar={
          <HealthBar
            fill={disk.kind === 'value' ? disk.percent : null}
            limit={FLEET_HEALTH_WARN_PERCENT}
            warning={diskFull}
            className={barClass}
          />
        }
        noData={t('no-data')}
      />
      <TileFigure
        label={memoryFull ? t('memory-full') : t('memory')}
        value={memory.kind === 'value' ? t('percent-value', { percent: memory.percent }) : null}
        warning={memoryFull}
        bar={
          <HealthBar
            fill={memory.kind === 'value' ? memory.percent : null}
            limit={FLEET_HEALTH_WARN_PERCENT}
            warning={memoryFull}
            className={barClass}
          />
        }
        noData={t('no-data')}
      />
      <TileFigure
        label={t('temperature')}
        value={temperature.kind === 'value' ? t('temperature-value', { degrees: temperature.degrees }) : null}
        warning={temperature.kind === 'value' && temperature.overLimit}
        bar={
          <HealthBar
            fill={temperature.kind === 'value' ? clampPercent(temperature.degrees) : null}
            limit={limits.temperatureC.throttleAt}
            resume={limits.temperatureC.resumeBelow}
            warning={temperature.kind === 'value' && temperature.overLimit}
            className={barClass}
          />
        }
        noData={t('no-data')}
      />
      <TileFigure
        label={t('load')}
        value={load.kind === 'value' ? t('load-value', { factor: formatFactor(load.factor) }) : null}
        warning={load.kind === 'value' && load.overLimit}
        bar={
          <HealthBar
            fill={load.kind === 'value' ? clampPercent((load.factor / LOAD_BAR_SCALE) * 100) : null}
            limit={(limits.loadPerCore.throttleAt / LOAD_BAR_SCALE) * 100}
            resume={(limits.loadPerCore.resumeBelow / LOAD_BAR_SCALE) * 100}
            warning={load.kind === 'value' && load.overLimit}
            className={barClass}
          />
        }
        noData={t('no-data')}
      />
    </div>
  );
}

function TileFigure({
  label,
  value,
  warning,
  bar,
  noData,
}: {
  readonly label: string;
  /** `null` = never reported. */
  readonly value: string | null;
  readonly warning: boolean;
  readonly bar: React.ReactNode;
  readonly noData: string;
}) {
  return (
    <span className="flex min-w-0 flex-col gap-1">
      <span className="flex items-baseline justify-between gap-2 text-sm">
        <span className={cn('min-w-0 break-words', warning ? 'font-medium text-warning-700' : 'text-text-tertiary')}>
          {label}
        </span>
        {value === null ? (
          <span className="shrink-0 whitespace-nowrap font-sans text-text-placeholder">{noData}</span>
        ) : (
          <span
            className={cn(
              'shrink-0 whitespace-nowrap font-mono tabular-nums',
              warning ? 'font-medium text-warning-700' : 'text-text-primary',
            )}
          >
            {value}
          </span>
        )}
      </span>
      {bar}
    </span>
  );
}
